import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Play, Clock, BookOpen, Users, Settings, Video, FileText, Award, Eye, CheckCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { TutorialPlayer } from "@/components/tutorials/TutorialPlayer";
import { TutorialManagement } from "@/components/tutorials/TutorialManagement";
import ResourceManager from "@/components/resources/ResourceManager";

interface Tutorial {
  id: string;
  title: string;
  description: string;
  duration_minutes: number;
  difficulty: string;
  category: string;
  is_active: boolean;
  created_at: string;
  created_by?: string;
}

interface TutorialProgress {
  id: string;
  user_id: string;
  tutorial_id: string;
  step_id: string;
  completed_at: string | null;
}

export default function CourseCreationTutorials() {
  const [selectedTutorial, setSelectedTutorial] = useState<Tutorial | null>(null);
  const [previewTutorial, setPreviewTutorial] = useState<Tutorial | null>(null);
  const [tutorials, setTutorials] = useState<Tutorial[]>([]);
  const [tutorialProgress, setTutorialProgress] = useState<Record<string, string[]>>({});
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all-tutorials");
  const { toast } = useToast();

  useEffect(() => {
    checkUserRole();
    loadTutorials();
    loadUserProgress();
  }, []);

  // Refresh progress when tutorials change
  useEffect(() => {
    if (tutorials.length > 0) {
      loadUserProgress();
    }
  }, [tutorials]);

  const checkUserRole = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from('profiles')
        .select('is_admin')
        .eq('id', user.id)
        .single();

      if (profile) {
        setIsAdmin(profile.is_admin || false);
      }
    } catch (error) {
      console.error('Error checking user role:', error);
    }
  };

  const loadTutorials = async () => {
    try {
      setLoading(true);
      
      // Create mock tutorials data
      const mockTutorials: Tutorial[] = [
        {
          id: 'getting-started',
          title: 'Getting Started with Course Creation',
          description: 'Learn the basics of creating effective professional development courses',
          duration_minutes: 30,
          difficulty: 'beginner',
          category: 'Foundation',
          is_active: true,
          created_at: new Date().toISOString(),
          created_by: 'system'
        },
        {
          id: 'content-development',
          title: 'Content Development Best Practices', 
          description: 'Master the art of creating engaging and effective course content',
          duration_minutes: 45,
          difficulty: 'intermediate',
          category: 'Content',
          is_active: true,
          created_at: new Date().toISOString(),
          created_by: 'system'
        },
        {
          id: 'online-delivery',
          title: 'Online Course Delivery',
          description: 'Master the technical and pedagogical aspects of online course delivery',
          duration_minutes: 40,
          difficulty: 'intermediate',
          category: 'Technology', 
          is_active: true,
          created_at: new Date().toISOString(),
          created_by: 'system'
        },
        {
          id: 'evaluation-improvement',
          title: 'Course Evaluation & Continuous Improvement',
          description: 'Learn how to evaluate course effectiveness and implement improvements',
          duration_minutes: 35,
          difficulty: 'advanced',
          category: 'Quality',
          is_active: true,
          created_at: new Date().toISOString(),
          created_by: 'system'
        }
      ];

      try {
        // Try to load from database first, but don't fail if it doesn't work
        const { data } = await supabase
          .from('tutorials' as any)
          .select('*')
          .eq('is_active', true)
          .order('created_at', { ascending: false });

        // Use database data if it exists and is valid, otherwise use mock data
        if (data && Array.isArray(data) && data.length > 0) {
          // Check if the data has the right structure
          const isValidTutorialData = data.every((item: any) => 
            item != null && 
            typeof item === 'object' && 
            typeof item.id === 'string' && 
            typeof item.title === 'string'
          );
          
          if (isValidTutorialData) {
            setTutorials(data as unknown as Tutorial[]);
          } else {
            setTutorials(mockTutorials);
          }
        } else {
          setTutorials(mockTutorials);
        }
      } catch (dbError) {
        // If database query fails, just use mock data
        console.log('Using mock tutorials data:', dbError);
        setTutorials(mockTutorials);
      }
    } catch (error) {
      console.error('Error loading tutorials:', error);
      setTutorials([]);
      toast({
        title: "Error",
        description: "Failed to load tutorials",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const loadUserProgress = async () => {
    // Load progress from localStorage for now
    const savedProgress = localStorage.getItem('tutorial-progress');
    if (savedProgress) {
      try {
        setTutorialProgress(JSON.parse(savedProgress));
      } catch (error) {
        console.error('Error loading progress:', error);
        setTutorialProgress({});
      }
    }
  };

  const handleProgressUpdate = (tutorialId: string, completedSteps: string[]) => {
    const newProgress = { ...tutorialProgress, [tutorialId]: completedSteps };
    setTutorialProgress(newProgress);
    localStorage.setItem('tutorial-progress', JSON.stringify(newProgress));
  };

  const getTutorialProgress = (tutorialId: string) => {
    const completedSteps = tutorialProgress[tutorialId] || [];
    // Assuming each tutorial has 3-4 steps based on the tutorial content
    const totalSteps = tutorialId === 'getting-started' ? 3 : 
                      tutorialId === 'content-development' ? 2 : 
                      tutorialId === 'online-delivery' ? 3 : 3;
    
    return Math.min((completedSteps.length / totalSteps) * 100, 100);
  };

  const getInProgressTutorials = () => {
    return tutorials.filter(tutorial => {
      const progress = getTutorialProgress(tutorial.id);
      return progress > 0 && progress < 100;
    });
  };

  const getCompletedTutorials = () => {
    return tutorials.filter(tutorial => {
      const progress = getTutorialProgress(tutorial.id);
      return progress >= 100;
    });
  };

  const getDifficultyBadge = (difficulty: string) => {
    const variants = {
      beginner: { variant: 'secondary' as const, color: 'bg-green-100 text-green-800' },
      intermediate: { variant: 'secondary' as const, color: 'bg-yellow-100 text-yellow-800' },
      advanced: { variant: 'secondary' as const, color: 'bg-red-100 text-red-800' }
    };

    const config = variants[difficulty as keyof typeof variants];
    
    return (
      <Badge className={config.color}>
        {difficulty.toUpperCase()}
      </Badge>
    );
  };

  const getCategoryIcon = (category: string) => {
    const icons = {
      'Foundation': BookOpen,
      'Content': FileText,
      'Technology': Video,
      'Quality': Award
    };
    
    const Icon = icons[category as keyof typeof icons] || BookOpen;
    return <Icon className="h-5 w-5" />;
  };


  return (
    <div className="container mx-auto p-6 space-y-6 print:hidden">
      <div className="text-center space-y-4 print:hidden">
        <h1 className="text-4xl font-bold bg-gradient-primary bg-clip-text text-transparent print:hidden">
          Course Creation Academy
        </h1>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto print:hidden">
          Master the art of creating engaging professional development courses with our comprehensive tutorials
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6 print:hidden">
        <TabsList className={`grid w-full ${isAdmin ? 'grid-cols-5' : 'grid-cols-4'} print:hidden`}>
          <TabsTrigger value="all-tutorials">All Tutorials</TabsTrigger>
          <TabsTrigger value="in-progress">In Progress</TabsTrigger>
          <TabsTrigger value="completed">Completed</TabsTrigger>
          <TabsTrigger value="resources">Resources</TabsTrigger>
          {isAdmin && <TabsTrigger value="admin">Admin</TabsTrigger>}
        </TabsList>

        <TabsContent value="all-tutorials" className="space-y-6">
          {loading ? (
            <div className="text-center py-8">Loading tutorials...</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {tutorials.map((tutorial) => {
                const progress = getTutorialProgress(tutorial.id);
                return (
                  <Card key={tutorial.id} className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <div className="flex items-center gap-3">
                          {getCategoryIcon(tutorial.category)}
                          <div>
                            <CardTitle className="text-lg">{tutorial.title}</CardTitle>
                            <CardDescription className="mt-1">
                              {tutorial.description}
                            </CardDescription>
                          </div>
                        </div>
                        {getDifficultyBadge(tutorial.difficulty)}
                      </div>
                    </CardHeader>
                    
                    <CardContent className="space-y-4">
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {tutorial.duration_minutes} min
                        </div>
                        <Badge variant="outline">{tutorial.category}</Badge>
                      </div>

                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span>Progress</span>
                          <span>{Math.round(progress)}%</span>
                        </div>
                        <Progress value={progress} className="h-2" />
                      </div>

                      <div className="flex gap-2">
                        <Button 
                          className="flex-1 gap-2"
                          onClick={() => setSelectedTutorial(tutorial)}
                        >
                          <Play className="h-4 w-4" />
                          {progress >= 100 ? 'Review' : progress > 0 ? 'Resume' : 'Start'} Tutorial
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setPreviewTutorial(tutorial)}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          Preview
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
              
              {tutorials.length === 0 && !loading && (
                <div className="col-span-full text-center text-muted-foreground py-8">
                  No tutorials available yet.
                </div>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="in-progress" className="space-y-6">
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {getInProgressTutorials().map((tutorial) => {
                const progress = getTutorialProgress(tutorial.id);
                return (
                  <Card key={tutorial.id} className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="text-lg">{tutorial.title}</CardTitle>
                          <CardDescription className="mt-1">
                            {tutorial.description}
                          </CardDescription>
                        </div>
                        {getDifficultyBadge(tutorial.difficulty)}
                      </div>
                    </CardHeader>
                    
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span>Progress</span>
                          <span>{Math.round(progress)}%</span>
                        </div>
                        <Progress value={progress} className="h-2" />
                      </div>

                      <Button 
                        className="w-full gap-2"
                        onClick={() => setSelectedTutorial(tutorial)}
                      >
                        <Play className="h-4 w-4" />
                        Resume Tutorial
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
              
              {getInProgressTutorials().length === 0 && (
                <div className="col-span-full text-center text-muted-foreground py-8">
                  No tutorials in progress. Start a tutorial to see it here.
                </div>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="completed" className="space-y-6">
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {getCompletedTutorials().map((tutorial) => (
                <Card key={tutorial.id} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-lg">{tutorial.title}</CardTitle>
                        <CardDescription className="mt-1">
                          {tutorial.description}
                        </CardDescription>
                      </div>
                      <Badge className="bg-green-100 text-green-800">
                        Completed
                      </Badge>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="space-y-4">
                    <Progress value={100} className="h-2" />
                    
                    <div className="flex gap-2">
                      <Button 
                        className="flex-1 gap-2"
                        variant="outline"
                        onClick={() => setSelectedTutorial(tutorial)}
                      >
                        <Eye className="h-4 w-4" />
                        Review Tutorial
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              
              {getCompletedTutorials().length === 0 && (
                <div className="col-span-full text-center text-muted-foreground py-8">
                  No completed tutorials yet. Complete a tutorial to see it here.
                </div>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="resources" className="space-y-6">
          <ResourceManager />
        </TabsContent>

        {isAdmin && (
          <TabsContent value="admin">
            <TutorialManagement />
          </TabsContent>
        )}
      </Tabs>

      {/* Tutorial Player Modal */}
        {selectedTutorial && (
          <TutorialPlayer 
            tutorial={selectedTutorial} 
            onClose={() => setSelectedTutorial(null)}
            onProgressUpdate={handleProgressUpdate}
          />
        )}

      {/* Tutorial Preview Modal */}
      {previewTutorial && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <CardHeader>
              <div className="flex justify-between items-start">
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <CardTitle className="text-2xl">{previewTutorial.title}</CardTitle>
                    {getDifficultyBadge(previewTutorial.difficulty)}
                  </div>
                  <CardDescription>
                    {previewTutorial.description}
                  </CardDescription>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      {previewTutorial.duration_minutes} min
                    </div>
                    <Badge variant="outline">{previewTutorial.category}</Badge>
                  </div>
                </div>
                <Button variant="ghost" onClick={() => setPreviewTutorial(null)}>
                  ×
                </Button>
              </div>
            </CardHeader>
            
            <CardContent className="space-y-6">
              <div className="text-center">
                <p className="text-muted-foreground mb-4">
                  Ready to start this tutorial? Click below to begin your learning journey.
                </p>
                <div className="flex gap-2 justify-center">
                  <Button 
                    onClick={() => {
                      setSelectedTutorial(previewTutorial);
                      setPreviewTutorial(null);
                    }}
                    className="gap-2"
                  >
                    <Play className="h-4 w-4" />
                    Start Tutorial
                  </Button>
                  <Button variant="outline" onClick={() => setPreviewTutorial(null)}>
                    Close Preview
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}